import {DataFrameRow, HydratedProperties} from 'data-frames/DataFrameTypes';
import {assertLeafTypeNumerical, LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {deserializeDecimalToFloat} from 'data-types/util/Deserializers';
import {JSONValue} from 'util/Types';

import {DataFrameValueAccessor} from './createAccessors';
import resolveDataFrameValue, {DataFrameResolutionConfig} from './resolveDataFrameValue';
import {ColumnTypeValidationOptions} from './validateColumnType';

/**
 * Utility type for defining maps of columns that a function accepts to columns in the caller's data frames. This
 * can then be used with `createAccessors` to create accessor functions.
 *
 * This type helps follow the column accessor pattern where instead of prescribing data frames have specific columns,
 * we prescribe callers to define which ones of their data frame's columns match with the function's required
 * columns.
 *
 * Example usage:
 * ```
 *    type ScatterPlotColumns: 'x' | 'y';
 *    interface ScatterPlotProps {
 *      dataFrame: DataFrame;
 *      columnsMapping: ColumnsMapping<ScatterPlotColumns>;
 *    }
 *
 *    function ScatterPlot({dataFrame, columnsMapping}: ScatterPlotProps) {
 *      [...]
 *      const xValue = dataFrame.rows[0][columnsMapping.x]
 *    }
 * ```
 */
export type ColumnsMapping<RequiredColumns extends string, OptionalColumns extends string = never> = Record<
  RequiredColumns,
  string
> &
  Partial<Record<OptionalColumns, string>>;

/**
 * Utility type for defining maps of columns with their `ColumnTypeValidationOptions`. This can be used with
 * `createAccessors` to create type validated accessor functions.
 *
 * This type is a useful addition to `ColumnsMapping` for when you want to prescribe that the input data frame
 * of a function not only has certain assigned columns, but that those assigned columns also have certain types.
 *
 * For example, to render a `ScatterPlot`, we not only want the caller to map their data frame's columns to
 * `x` and `y`, but we might also want to validate that they're mapping numerical columns to those.
 */
export type ColumnValidationMap<ChartColumns extends string> = Record<
  ChartColumns,
  ColumnTypeValidationOptions
>;

/**
 * Higher-order function for creating accessor functions that return JavaScript `number`s from numeric data frame
 * columns.
 *
 * For cases where the "number" is serialized as a string, we deserialize to a number, and in cases where the
 * number is serialized as a number, we simply return the value.
 *
 * Example usage:
 * ```
 * const {accessor, validatedLeafType} = createAccessor(dataFrame, "x");
 * const xAccessor = numericAccessor(accessor, leafType);
 * ```
 */
export function numericAccessor(
  accessor: (row: DataFrameRow) => JSONScalar,
  leafType: LeafType
): DataFrameValueAccessor<number> {
  assertLeafTypeNumerical(leafType);

  return (row: DataFrameRow) => {
    const value = accessor(row);

    return typeof value === 'string' ? deserializeDecimalToFloat(value) : (value as number);
  };
}

/**
 * Higher-order function for creating accessor functions that return JavaScript `number`s or `null` from numeric data frame
 * columns.
 *
 * For cases where the "number" is serialized as a string, we deserialize to a number, and in cases where the
 * number is serialized as a number, we simply return the value.
 *
 * Example usage:
 * ```
 * const {accessor, validatedLeafType} = createAccessor(dataFrame, "x");
 * const xAccessor = nullableNumericAccessor(accessor, leafType);
 * ```
 */
export function nullableNumericAccessor(
  accessor: (row: DataFrameRow) => JSONScalar,
  leafType: LeafType
): DataFrameValueAccessor<number | null> {
  assertLeafTypeNumerical(leafType);

  return (row: DataFrameRow) => {
    const value = accessor(row);

    return value == null
      ? null
      : typeof value === 'string'
        ? deserializeDecimalToFloat(value)
        : (value as number);
  };
}

/**
 * Higher-order function for creating accessor functions that return JavaScript `string`s from any column type
 * where those strings are resolved using the given config if there are hydrated properties available to resolve
 * them with.
 *
 * Example usage:
 * ```
 * const {accessor, validatedLeafType} = createAccessor(dataFrame, "x");
 * const labelAccessor = resolvedStringAccessor(accessor, dataFrame.hydrateProperties, RESOLUTION_CONFIG);
 * ```
 */
export function resolvedStringAccessor(
  accessor: (row: DataFrameRow) => JSONScalar,
  hydratedProperties: HydratedProperties,
  resolutionConfig: DataFrameResolutionConfig
): DataFrameValueAccessor<string> {
  return stringAccessor((row: DataFrameRow) =>
    resolveDataFrameValue(accessor(row), hydratedProperties, resolutionConfig)
  );
}

/**
 * Higher-order function for creating accessor functions that return JavaScript `string`s from any column type.
 *
 * Example usage:
 * ```
 * const {accessor, validatedLeafType} = createAccessor(dataFrame, "x");
 * const labelAccessor = stringAccessor(accessor);
 * ```
 */
export function stringAccessor(accessor: (row: DataFrameRow) => JSONScalar): DataFrameValueAccessor<string> {
  return (row: DataFrameRow) => String(accessor(row));
}

/**
 * Higher-order function for creating accessor functions that return JavaScript `Array<string>`s from array
 * column types.
 *
 * @example
 * ```
 * const {accessor, validatedLeafType} = createAccessor(dataFrame, "x");
 * const labelsAccessor = stringListAccessor(accessor);
 *
 * labelsAccessor(row)
 * => ['label_1', 'label_2', 'label_3']
 * ```
 */
export function stringListAccessor(
  accessor: (row: DataFrameRow) => JSONValue
): DataFrameValueAccessor<Array<string>> {
  return (row: DataFrameRow) => (accessor(row) as Array<JSONScalar>).map((value) => String(value));
}

/**
 * Higher-order function for creating accessor functions that return JavaScript `Date`s from datetime column
 * types.
 *
 * Note that this works for both DATE and DATETIME Benchling types, as they are treated the same in Javascript.
 *
 * @example
 * ```
 * const {accessor, validatedLeafType} = createAccessor(dataFrame, "datetime");
 * const datetimeAccessor = datetimeAccessor(accessor);
 * ...
 */
export function datetimeAccessor(accessor: (row: DataFrameRow) => JSONValue): DataFrameValueAccessor<Date> {
  return (row: DataFrameRow) => new Date(accessor(row) as string);
}
